// ============================================
// FILE: src/components/DesktopHomeScreen.jsx
// Full-width desktop home page. Reuses the same data, helpers and
// handlers as the mobile HomeScreen — only the presentation differs.
// Rendered (instead of HomeScreen) when viewport >= 1024px on the Home tab.
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Heart, Gift, Package, Plus,
  ShoppingCart, Flame, ChevronRight, ChevronLeft,
} from 'lucide-react';

import ProductDetailsModal from './modals/ProductDetailsModal';
import DesktopNavbar from './DesktopNavbar';
import DesktopFooter from './DesktopFooter';
import { categoryIcons } from '../data/products';
import { getProductImage, resolveProductImageUrl } from '../utils/imageHelpers';
import { getFinalPrice, hasVariants, getVariantPriceFrom } from '../utils/priceHelpers';
import { isCategoryVisible } from '../utils/categoryConfig';
import CategoryBannerCarousel from './CategoryBannerCarousel';
import CategoryShelves from './CategoryShelves';
import QuantityTextBox from './QuantityTextBox';
import { usesQuantityTextBox } from '../utils/quantityHelpers';

const DesktopHomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  selectedLocation,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  getCartTotal,
  updateQuantity,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  toggleFavorite,
  setActiveTab,
  selectedCategory = 'All',
  setSelectedCategory,
  categories: apiCategories = [],
  onCategoryClick,
  onOpenOffers,
  showToast,
  appSettings,
  handleLogout,
  featureSections = [],
  appTheme = 'blue',
  onNavigate,
  isAuthenticated = true,
  onLogin,
}) => {
  const navTo = onNavigate || setActiveTab;
  const favArray = Array.isArray(favorites) ? favorites : [];
  const isProductFavorited = (productId) => (
    favArray.some((item) => String(item?.id) === String(productId))
  );

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const productsPerPage = 30;

  const productsSectionRef = useRef(null);

  // Banner rotation now lives inside CategoryBannerCarousel, which owns the
  // slide set and restarts its own timer on manual navigation.

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    if (searchQuery && searchQuery.length > 0) {
      const timer = setTimeout(() => {
        productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // "All" with no search: one shelf per category, each with its own banners.
  const showCategoryShelves = selectedCategory === 'All' && !searchQuery && apiCategories.length > 0;

  const categories = apiCategories.length > 0
    ? ['All', ...apiCategories.map(c => c.name).filter(isCategoryVisible)]
    : ['All', ...new Set(products.map(p =>
        typeof p.category === 'object' && p.category?.name ? p.category.name : p.category || 'Uncategorized'
      ).filter(Boolean).filter(isCategoryVisible))];

  // ── Shared helpers (mirrors HomeScreen) ──
  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    return ['kg', 'gram', 'g'].includes(String(unit).toLowerCase().trim());
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = (product) => {
    const { unit } = getWeightParts(product);
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  };

  const formatWeightLabel = (product) => {
    const { value, unit } = getWeightParts(product);
    if (value) return `${value}${unit ? ` ${unit}` : ''}`;
    return product.weight ? String(product.weight) : '';
  };

  const getSelectedQuantity = (productId) => quantitySelections[productId] || 1;
  const setSelectedQuantity = (productId, qty) => {
    setQuantitySelections((prev) => ({ ...prev, [productId]: qty }));
  };
  const handleAddToCartWithQuantity = (product) => {
    addToCart(product, getSelectedQuantity(product.id));
  };

  // ── Product card (shared markup, reused in feature rows + grid) ──
  const renderProductCard = (product, index = 0) => {
    const isFav = isProductFavorited(product.id);
    const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
    const offPercent = hasDiscount
      ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
      : 0;
    return (
      <div key={product.id} className="rounded overflow-hidden relative product-card glass-card bg-white shadow-sm hover:shadow-xl transition-shadow"
        style={{ animationDelay: `${(index % 10) * 50}ms` }}>
        <div
          className="relative overflow-hidden h-48 cursor-pointer glass-image group"
          onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
        >
          {getProductImage(product, 'card')}
          {hasDiscount && (
            <span className="absolute top-3 left-3 text-xs bg-[#ff3f6c] text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
              {offPercent}% OFF
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
          >
            <Heart size={18} className={isFav ? 'text-pink-500' : 'text-[#94969f]'} fill={isFav ? 'currentColor' : 'none'} />
          </button>
        </div>

        <div className="p-3">
          <h3
            className="font-display font-semibold text-[#282c3f] text-sm mb-1 cursor-pointer hover:text-[#ff3f6c] transition-colors line-clamp-1"
            onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
          >{product.name}</h3>
          <div className="flex items-center gap-2 mb-2">
            {product.weight && (
              <span className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded">{formatWeightLabel(product)}</span>
            )}
            {!product.weight && product.fat && (
              <span className="text-xs bg-[#e8f7f3] text-[#03a685] px-2 py-1 rounded">{product.fat} fat</span>
            )}
            {!product.weight && !product.fat && product.brand && (
              <span className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded">{product.brand}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-2">
            {hasVariants(product) ? (
              <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">from ₹{getVariantPriceFrom(product)}</span>
            ) : product.sale_price && product.sale_price < product.price ? (
              <>
                <span className="text-sm font-extrabold text-[#03a685] drop-shadow-sm">₹{product.sale_price}</span>
                <span className="text-xs text-[#94969f] line-through">₹{product.price}</span>
              </>
            ) : (
              <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">₹{getFinalPrice(product)}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {usesQuantityTextBox(product) ? (
                <QuantityTextBox
                  value={getSelectedQuantity(product.id)}
                  onChange={(qty) => setSelectedQuantity(product.id, qty)}
                />
              ) : (
                getQuantityOptions(product).map((qty) => (
                  <button
                    key={qty}
                    type="button"
                    onClick={() => setSelectedQuantity(product.id, qty)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      getSelectedQuantity(product.id) === qty
                        ? 'bg-[#ff3f6c] text-white border-[#ff3f6c]'
                        : 'bg-white text-[#535766] border-[#eaeaec] hover:border-[#ff3f6c]'
                    }`}
                  >
                    {qty}
                  </button>
                ))
              )}
              {isWeightUnit(getWeightParts(product).unit) && (
                <span className="text-xs text-[#535766] ml-1 font-medium">Kg</span>
              )}
              {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                <span className="text-xs text-[#535766] ml-1 font-medium">{formatUnit(getWeightParts(product).unit)}</span>
              )}
            </div>

            {(() => {
              const cartItem = cart.find(item => item.id === product.id);
              if (cartItem && cartItem.quantity > 0) {
                return (
                  <div className="w-full flex items-center justify-center gap-2 bg-[#fff8fa] border border-[#ff3f6c] rounded-lg px-2 py-1 mt-1">
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] text-xl font-bold shadow-sm hover:bg-[#fff8fa] focus:outline-none disabled:opacity-50"
                      disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                      onClick={async () => {
                        if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                          setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                          await updateQuantity(product.id, -1);
                          setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                        }
                      }}
                    >
                      <span>&#8722;</span>
                    </button>
                    <span className="text-base font-bold text-[#ff3f6c] w-10 text-center select-none">
                      {quantityLoading[product.id] ? '...' : cartItem.quantity}
                    </span>
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] text-xl font-bold shadow-sm hover:bg-[#fff8fa] focus:outline-none disabled:opacity-50"
                      disabled={!!quantityLoading[product.id]}
                      onClick={async () => {
                        if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                          setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                          await updateQuantity(product.id, 1);
                          setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                        }
                      }}
                    >
                      <span>&#43;</span>
                    </button>
                  </div>
                );
              }
              return (
                <button
                  onClick={() => handleAddToCartWithQuantity(product)}
                  className="w-full bg-[#ff3f6c] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#e03a60] transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={16} />
                  Add to Cart
                </button>
              );
            })()}
          </div>

          {Boolean(Number(product.is_subscribable)) && (
            <button
              onClick={() => setShowSubscription(product)}
              className="w-full bg-[#03a685] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#028f73] transition-colors mt-2"
            >
              Subscribe &amp; Save
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: '#f7f7f7', color: '#1b1b1b', minHeight: '100vh' }}>

      {/* ── Shared sticky desktop navbar ── */}
      <DesktopNavbar
        appSettings={appSettings}
        userProfile={userProfile}
        activeTab="home"
        setActiveTab={navTo}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedLocation={selectedLocation}
        setShowLocationModal={setShowLocationModal}
        notifications={notifications}
        favorites={favorites}
        getCartCount={getCartCount}
        setShowNotifications={setShowNotifications}
        setShowFavorites={setShowFavorites}
        handleLogout={handleLogout}
        isAuthenticated={isAuthenticated}
        onLogin={onLogin}
      />

      <main className="max-w-[1280px] mx-auto px-8 py-6">
        {/* Greeting + quick action pills */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <p className="text-sm text-[#94969f]">
            {userProfile?.name
              ? <>Hey <span className="font-semibold text-[#535766]">{userProfile.name}</span> 👋 — fresh picks &amp; daily savings</>
              : <>Fresh picks &amp; daily savings</>}
          </p>
          <div className="flex items-center gap-3">
            {[
              { icon: <Gift size={14} />, label: 'Offers', action: () => onOpenOffers && onOpenOffers('all') },
              { icon: <Package size={14} />, label: 'Track Order', action: () => setActiveTab && setActiveTab('orders') },
              { icon: <span className="text-xs">🔔</span>, label: 'Subscribe', action: () => setShowSubscription(true) },
            ].map(({ icon, label, action }) => (
              <button key={label} onClick={action}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full whitespace-nowrap text-sm font-medium text-[#535766] transition-all hover:bg-[#fff8fa]"
                style={{ background: '#fff', border: '1.5px solid #e8e8e8' }}
              >
                <span style={{ color: '#ff3f6c' }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Hero carousel — category-scoped banners from the admin ── */}
        <CategoryBannerCarousel
          categories={apiCategories}
          selectedCategory={selectedCategory}
          onCategoryClick={onCategoryClick}
          setSelectedCategory={setSelectedCategory}
          isDesktop={true}
        />

        {/* ── Shop by Category ── */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[#282c3f] font-display">Shop by Category</h2>
            <span className="text-sm text-[#94969f]">{categories.length - 1} types</span>
          </div>
          <div className="flex gap-5 overflow-x-auto pb-2 hide-scrollbar">
            {categories.map(category => {
              const isActive = selectedCategory === category;
              const catPalettes = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#FFEAA7','#DDA0DD','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9'];
              const idx = categories.indexOf(category);
              const circleColor = isActive ? '#ff3f6c' : catPalettes[idx % catPalettes.length];
              const catObj = category !== 'All' ? apiCategories.find(c => c.name === category) : null;
              const catImgUrl = catObj ? resolveProductImageUrl(catObj.image) : null;
              return (
                <button
                  key={category}
                  onClick={() => {
                    if (category === 'All') {
                      setSelectedCategory && setSelectedCategory('All');
                    } else if (catObj && onCategoryClick) {
                      onCategoryClick(catObj);
                    } else {
                      setSelectedCategory && setSelectedCategory(category);
                    }
                  }}
                  className="flex flex-col items-center gap-2 flex-shrink-0 transition-all"
                  style={{ minWidth: 88 }}
                >
                  <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-sm transition-all relative overflow-hidden"
                    style={{
                      background: isActive ? 'linear-gradient(135deg,#ff3f6c,#e03a60)' : `${circleColor}22`,
                      border: isActive ? '3px solid #ff3f6c' : `2px solid ${circleColor}55`,
                      transform: isActive ? 'scale(1.06)' : 'scale(1)',
                    }}>
                    {catImgUrl && (
                      <img
                        src={catImgUrl}
                        alt={category}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {category === 'All'
                      ? <ShoppingCart size={30} style={{ color: isActive ? '#fff' : circleColor }} strokeWidth={2} />
                      : <span className="text-3xl leading-none">{categoryIcons[category] || '📦'}</span>
                    }
                  </div>
                  <span className="text-sm font-semibold text-center leading-tight"
                    style={{ color: isActive ? '#ff3f6c' : '#555', maxWidth: 88 }}>
                    {category}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Feature Sections ── */}
        {Array.isArray(featureSections) && featureSections.map(section => {
          const sectionColor = section.color || '#3b82f6';
          const sectionProducts = section.products || [];
          return (
            <section key={section.id} className="mb-12">
              <div className="rounded overflow-hidden shadow-sm mb-4">
                <div
                  className="flex items-center justify-between px-5 py-4"
                  style={{ background: `linear-gradient(135deg, ${sectionColor}22 0%, ${sectionColor}08 100%)`, borderLeft: `4px solid ${sectionColor}` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-11 h-11 rounded text-white shadow-md"
                      style={{ background: `linear-gradient(135deg, ${sectionColor}, ${sectionColor}99)` }}>
                      {section.icon ? <i className={`${section.icon} text-base`} /> : <Flame size={18} />}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-[#282c3f] leading-tight">{section.name}</h2>
                      <p className="text-xs text-[#94969f]">{sectionProducts.length} items available</p>
                    </div>
                    <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold text-white ml-2"
                      style={{ backgroundColor: sectionColor }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white/80 motion-safe:animate-pulse inline-block" />
                      LIVE
                    </span>
                  </div>
                  <button
                    onClick={() => onOpenOffers && onOpenOffers(section.id)}
                    className="flex items-center gap-0.5 text-sm font-semibold active:scale-95 transition-transform"
                    style={{ color: sectionColor }}
                  >
                    View All <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-5">
                {sectionProducts.slice(0, 5).map((product, index) => renderProductCard(product, index))}
              </div>
            </section>
          );
        })}

        {/* ── Products — per-category shelves on "All", flat grid otherwise ── */}
        {(() => {
        const productsGrid = (
        <section className="products-section" ref={productsSectionRef}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#282c3f] font-display">
              {selectedCategory === 'All' ? 'Our Products' : selectedCategory}
            </h2>
            <span className="text-sm text-[#94969f]">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            </span>
          </div>

          {paginatedProducts.length === 0 ? (
            <div className="text-center py-20 text-[#94969f]">
              <Package size={48} className="mx-auto mb-4 opacity-40" />
              <p className="text-lg font-medium">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {paginatedProducts.map((product, index) => renderProductCard(product, index))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${currentPage === 1 ? 'bg-gray-200 text-[#94969f] cursor-not-allowed' : 'bg-[#ff3f6c] text-white hover:bg-[#e03a60]'}`}
              >
                <ChevronLeft size={18} /> Previous
              </button>
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${currentPage === page ? 'bg-[#ff3f6c] text-white shadow-lg' : 'bg-white text-[#535766] hover:bg-gray-100'}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${currentPage === totalPages ? 'bg-gray-200 text-[#94969f] cursor-not-allowed' : 'bg-[#ff3f6c] text-white hover:bg-[#e03a60]'}`}
              >
                Next <ChevronRight size={18} />
              </button>
            </div>
          )}
          {totalPages > 1 && (
            <p className="text-center text-sm text-[#94969f] mt-4">
              Showing {startIndex + 1}–{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
            </p>
          )}
        </section>
        );
        return showCategoryShelves ? (
          <CategoryShelves
            categories={apiCategories}
            products={filteredProducts}
            onCategoryClick={onCategoryClick}
            setSelectedCategory={setSelectedCategory}
            renderProductCard={renderProductCard}
            isDesktop
            fallback={productsGrid}
          />
        ) : productsGrid;
        })()}
      </main>

      {/* ── Shared Desktop Footer ── */}
      <DesktopFooter
        appSettings={appSettings}
        setActiveTab={navTo}
        setShowFavorites={setShowFavorites}
        setSelectedCategory={setSelectedCategory}
        onOpenOffers={onOpenOffers}
      />

      {/* Floating Checkout */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-8 right-8 bg-green-100 bg-green-500 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
            <span className="text-xs font-semibold mt-1 bg-[#ff3f6c] bg-opacity-70 px-2 py-0.5 rounded">
              Total: ₹{typeof getCartTotal === 'function' ? getCartTotal() : cart.reduce((sum, item) => {
                const p = (item.sale_price && item.sale_price < item.price) ? item.sale_price : item.price;
                return sum + (p * item.quantity);
              }, 0)}
            </span>
          </div>
        </button>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        cart={cart}
        getCartCount={getCartCount}
        getCartTotal={getCartTotal}
        openCheckout={openCheckout}
        isOpen={showProductDetails}
        onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
        appTheme={appTheme}
      />
    </div>
  );
};

export default DesktopHomeScreen;
